import MoveInput from "../Components/MoveInput/MoveInput";
import { Checkbox, MultiSelect } from "../../../BP2Components";
import { Icon_Close, Icon_Trash, Icon_ContentCopy } from "../../../BP2Components/Icons/icons";

import "./ElementSettings.less";
import EditableField from "../EditableField/EditableField";
import IntlMessages from "../../../../util/IntlMessages";
import { getDecorators } from "../../../../util/getDecorators";
import NonPrimitiveField from "./NonPrimitiveFields";

import ModelFieldEditor from "./ModelFieldEditor";

const DATE_FORMAT_OPTIONS = [
   { value: "yyyy-MM-dd", label: "yyyy-MM-dd  (2025-03-26)" },
   { value: "dd-MM-yyyy", label: "dd-MM-yyyy  (26-03-2025)" },
   { value: "yyyy.MM.dd", label: "yyyy.MM.dd  (2025.03.26)" },
   { value: "dd.MM.yyyy", label: "dd.MM.yyyy  (26.03.2025)" },
   { value: "yyyy/MM/dd", label: "yyyy/MM/dd  (2025/03/26)" },
   { value: "dd/MM/yyyy", label: "dd/MM/yyyy  (26/03/2025)" },
];

const DATETIME_FORMAT_OPTIONS = [
   ...DATE_FORMAT_OPTIONS,
   { value: "yyyy-MM-dd HH:mm", label: "yyyy-MM-dd HH:mm  (2025-03-26 14:30)" },
   { value: "dd-MM-yyyy HH:mm", label: "dd-MM-yyyy HH:mm  (26-03-2025 14:30)" },
   { value: "yyyy.MM.dd HH:mm", label: "yyyy.MM.dd HH:mm  (2025.03.26 14:30)" },
   { value: "dd.MM.yyyy HH:mm", label: "dd.MM.yyyy HH:mm  (26.03.2025 14:30)" },
   { value: "yyyy/MM/dd HH:mm", label: "yyyy/MM/dd HH:mm  (2025/03/26 14:30)" },
   { value: "dd/MM/yyyy HH:mm", label: "dd/MM/yyyy HH:mm  (26/03/2025 14:30)" },
];

const getDecimalPlaces = (format) => {
   if (!format) return "";
   if (format === "#") return "0";
   const match = format.match(/^#\.(0+)$/);
   return match ? String(match[1].length) : "";
};

const MAX_DECIMAL_PLACES = 10;

const buildDecimalFormat = (decimalPlaces) => {
   const decimals = parseInt(decimalPlaces, 10);
   if (isNaN(decimals) || decimals < 0 || decimals > MAX_DECIMAL_PLACES) return "";
   if (decimals === 0) return "#";
   return "#." + "0".repeat(decimals);
};

const getItemFullType = (item) => {
   if (typeof item?.type === "string" && item.type.includes(".")) return item.type;
   if (item?.modelField?.type?.module && item?.modelField?.type?.name) {
      return `${item.modelField.type.module}.${item.modelField.type.name}`;
   }
   return "";
};

const ElementSettings = ({ item, onAction, onClose }) => {
   const isSubItem = item.parentIndex || item.parentIndex === 0;
   const decoratorMap = getDecorators(item.decorators);
   const fullType = getItemFullType(item);

   const edit = decoratorMap?.EditableDecorator?.edit ?? false;
   const required = decoratorMap?.RequiredDecorator?.required ?? false;
   const hide = decoratorMap?.HideFieldDecorator?.hide ?? false;
   const valueFormat = decoratorMap?.ValueFormatDecorator?.format ?? "";

   const isNumericType = ["primitives.BigDecimal", "primitives.Integer"].includes(fullType);
   const isDateType = fullType === "primitives.LocalDate";
   const isDateTimeType = fullType === "primitives.LocalDateTime";

   return (
      <div className="element-settings">
         <div className="element-settings-close" onClick={onClose}>
            <Icon_Close fill="none" stroke="#64748B" />
         </div>
         <div className="two-cols">
            <div className="element-settings-name">
               <span className="label">hId: </span>
               <EditableField
                  fieldValue={item.hid || item.name}
                  onAccept={(value) => {
                     onAction("edit", { hid: value });
                  }}
               />
            </div>

            <div className="form-builder-element-settings-label">
               <span className="label">Label:</span>

               <EditableField
                  fieldValue={item.label}
                  onAccept={(value) => {
                     onAction("edit", { label: value });
                  }}
               />
            </div>
         </div>
         <div className="two-cols">
            {["primitives.Boolean", "primitives.LocalDate"].includes(fullType) ? null : (
               <div className="form-builder-element-settings-placeholder">
                  <span className="label">Placeholder:</span>

                  <EditableField
                     fieldValue={item.placeholder}
                     onAccept={(value) => onAction("edit", { placeholder: value })}
                  />
               </div>
            )}

            <div className="form-builder-element-settings-required">
               <span className="label">Pole wymagane:</span>
               <Checkbox
                  className="checkbox"
                  onChange={(e) => onAction("edit", { required: e.target.checked })}
                  checked={required}
                  defaultChecked={false}
               />
            </div>
         </div>
         <div className="two-cols">
            <div className="form-builder-element-settings-required">
               <span className="label">
                  <IntlMessages id="form.hiddenField" />:
               </span>
               <Checkbox
                  className="checkbox"
                  onChange={(e) => {
                     const isHidden = e.target.checked;

                     onAction("edit", { hide: isHidden });

                     if (isHidden) {
                        onAction("edit", { edit: false });
                     }
                  }}
                  checked={hide}
               />
            </div>

            <div className="form-builder-element-settings-required">
               <span className="label">Pole edytowalne:</span>
               <Checkbox
                  className="checkbox"
                  onChange={(e) => {
                     const isEditable = e.target.checked;

                     onAction("edit", { edit: isEditable });

                     if (isEditable) {
                        onAction("edit", { hide: false });
                     }
                  }}
                  checked={edit}
               />
            </div>
         </div>

         {isNumericType && (
            <div className="two-cols">
               <div className="form-builder-element-settings-required">
                  <span className="label">Miejsca po przecinku:</span>
                  <input
                     type="number"
                     min="0"
                     max="10"
                     className="form-builder-decimal-input"
                     value={getDecimalPlaces(valueFormat)}
                     placeholder="–"
                     onChange={(e) => {
                        const format = buildDecimalFormat(e.target.value);
                        onAction("edit", { format });
                     }}
                  />
               </div>
            </div>
         )}

         {(isDateType || isDateTimeType) && (
            <div className="two-cols">
               <div className="form-builder-element-settings-required">
                  <span className="label">Format daty:</span>
                  <MultiSelect
                     name="dateFormat"
                     isMulti={false}
                     isCreatable={false}
                     closeMenuOnSelect
                     isClearable
                     size="medium"
                     placeholder="Domyślny"
                     noOptionsMessage="Brak opcji"
                     options={isDateTimeType ? DATETIME_FORMAT_OPTIONS : DATE_FORMAT_OPTIONS}
                     value={
                        valueFormat
                           ? ((isDateTimeType ? DATETIME_FORMAT_OPTIONS : DATE_FORMAT_OPTIONS).find(
                                (o) => o.value === valueFormat,
                             ) ?? null)
                           : null
                     }
                     onChange={(opt) => onAction("edit", { format: opt?.value ?? "" })}
                  />
               </div>
            </div>
         )}

         {fullType === "primitives.ListTable" && <ModelFieldEditor item={item} />}

         <div className="form-edit-input-actions">
            <span className="label">Akcje: </span>

            {!isSubItem && <MoveInput onMove={(direction) => onAction("move", { direction })} />}

            <div className="form-builder-input-actions-common">
               <button
                  className="form-builder-duplicate-input"
                  onClick={() => {
                     onAction("duplicate");
                  }}
               >
                  <Icon_ContentCopy />
               </button>

               <button
                  className="form-delete-input"
                  onClick={() => {
                     onAction("delete");
                     onClose();
                  }}
               >
                  <Icon_Trash />
               </button>
            </div>
         </div>

         {!item?.isPrimitive && <NonPrimitiveField item={item} />}
      </div>
   );
};

export default ElementSettings;
